import React, { useEffect, useState, useContext } from "react";
import { useFormik } from "formik";
import {
  ProductInterface,
  TopSuppliers,
  validateProduct,
  ProductType,
  ObjectStatusType,
  CategoryType,
} from "ts-interfaces";
import {
  Avatar,
  Button,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Box,
  FormLabel,
} from "@mui/material";
import { Stack } from "@mui/system";
import { generateID } from "ui";
import AuthModal from "../studioxr/auth/AuthModal";
import { updateRecord, uploadFileToGCloud } from "../libs/firebase";
import { produce } from "immer";
import { MaterialCategory } from "../../../../packages/ts-interfaces";
import Autocomplete from "@mui/material/Autocomplete";
import { materialCreator } from "./MaterialCreator";
import { getPreset, presetMaps } from "ui";
import * as Sentry from "@sentry/react";
import { AuthContext } from "../auth/Auth";
import { base64ToBlob } from "./../libs/util";
import { ActionButton } from "../buttons/ButtonStyles";
import { ColorPicker } from "./ColorPicker";

interface FormValues {
  name: string;
  url: string;
  brand: string;
  color: string;
  preset: string;
  category: MaterialCategory | CategoryType;
}

const textFieldProps = {
  variant: "outlined" as const,
  margin: "normal" as const,
  size: "small" as const,
  fullWidth: true,
  type: "text",
  sx: { paddingRight: "10px", width: "98%", marginTop: "8px" },
};

interface PaintCreatorProps {
  incomingProduct?: ProductInterface;
  onSavedProduct: (product: ProductInterface) => void;
}

export const PaintCreator = ({
  incomingProduct,
  onSavedProduct,
}: PaintCreatorProps) => {
  const { currentUser } = useContext(AuthContext);
  const modalState = materialCreator((state) => state.modalState);
  const setModalState = materialCreator((state) => state.setModalState);
  const saving = materialCreator((state) => state.saving);
  const setSaving = materialCreator((state) => state.setSaving);
  const [product, setProduct] = useState<ProductInterface>(
    {} as ProductInterface
  );
  const showModal = modalState === "paint_metadata";
  // We're keeping the uploadedFile state for potential future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const formik = useFormik<FormValues>({
    initialValues: {
      name: "New Paint",
      url: "",
      brand: "",
      color: "#1976d2",
      preset: Object.keys(presetMaps)[0],
      category: MaterialCategory.PAINT,
    },
    onSubmit: () => {
      // Form is submitted programmatically via handleSubmit
    },
  });

  useEffect(() => {
    if (saving || !incomingProduct || incomingProduct?.uid == null) {
      return;
    }

    formik.setValues({
      name: incomingProduct.name || "Material",
      category: incomingProduct.category?.[0] || MaterialCategory.PAINT,
      brand: incomingProduct.metadata?.brand || "",
      url: incomingProduct.metadata?.url || "",
      color: incomingProduct.materialData?.materialProps?.color
        ? `#${incomingProduct.materialData?.materialProps?.color.toString(16)}`
        : "#ffffff",
      preset:
        incomingProduct.materialData?.preset || Object.keys(presetMaps)[0],
    });

    setProduct(incomingProduct);
  }, [incomingProduct, saving]);

  const handleImageUpload = (file: File) => {
    // Store the file for later upload to Firebase
    setUploadedFile(file);
    console.log("Image uploaded:", file);
  };

  const handleColorExtracted = (hexColor: string) => {
    // Update the color in the form
    formik.setFieldValue("color", hexColor);
    console.log("Color selected:", hexColor);
  };

  async function handleSubmit() {
    if (formik.values.name.length < 3) {
      return alert("Please enter a name for your paint");
    }

    setSaving(true);

    try {
      if (!currentUser?.uid) {
        throw new Error("Not logged in");
      }

      const isNewProduct = !product?.id;
      const id = product?.id || generateID();
      const colorInt = parseInt(formik.values.color.replace(/^#/, ""), 16);
      let paintUrl = product?.materialData?.files?.color_original;

      if (!paintUrl) {
        // Always create canvas with color swatch as thumbnail, regardless of source
        const canvas = document.createElement("canvas");
        canvas.width = 100;
        canvas.height = 100;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.fillStyle = formik.values.color;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL("image/jpeg"); // Get base64 string of the image
          const base64Data = dataUrl.split(",")[1]; // Remove the data URL scheme part
          const blob = base64ToBlob(base64Data, "image/jpeg"); // Convert to Blob

          // save canvas image to firebase storage
          paintUrl = await uploadFileToGCloud(
            `userImages/${currentUser.uid}/${id}`,
            id,
            blob
          );
        }
      }

      const materialData = {
        files: { color_original: paintUrl },
        id: id,
        materialProps: { color: colorInt },
        name: formik.values.name,
        preset: formik.values.preset,
        presets: [
          "behr-flat",
          "behr-matte",
          "behr-eggshell-enamel",
          "behr-satin-enamel",
          "behr-semi-gloss-enamel",
          "behr-high-gloss-enamel",
        ],
      };

      const newProduct = produce(product, (draft) => {
        draft.name = formik.values.name;
        draft.materialData = materialData;
        if (!draft.metadata) draft.metadata = {};
        draft.metadata.brand = formik.values.brand;
        draft.metadata.url = formik.values.url;
        draft.productType = ProductType.PAINT;
        draft.objectStatus = ObjectStatusType.USER_CREATED;
        if (!draft.id) draft.id = id;
        if (!draft.uid) draft.uid = currentUser.uid;
        draft.isStaticObject = true;
        draft.updatedAt = Date.now();
        draft.primaryColorInt = colorInt;
      });

      console.log(newProduct);
      validateProduct(newProduct);
      setProduct(newProduct);

      const result = await updateRecord(
        newProduct.id,
        "userProducts",
        newProduct
      );

      if (window.gtag && isNewProduct) {
        window.gtag("event", "custom_paint_created");
      }

      console.log("saved! ", result);
      onSavedProduct(newProduct);
    } catch (error) {
      console.log("oops!!", error);
      Sentry.captureException(error);
    } finally {
      setSaving(false);
      setModalState(null);
    }
  }

  const handleBrandChange = (
    _event: React.SyntheticEvent,
    value: string | null
  ) => {
    formik.setFieldValue("brand", value || "");
  };

  const handleClose = () => {
    setModalState(null);
  };

  const handlePresetChange = (presetKey: string) => {
    formik.setFieldValue("preset", presetKey);
  };

  if (!showModal) return null;

  return (
    <AuthModal open={showModal} onClose={handleClose}>
      <Stack sx={{ paddingLeft: "10px", paddingRight: "10px" }}>
        <Grid
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <Stack>
            <DialogTitle sx={{ paddingLeft: "0px", fontWeight: "bold" }}>
              Paint Details
            </DialogTitle>
            <Typography
              sx={{
                paddingLeft: "0px",
                marginTop: "-18px",
                color: "text.secondary",
                fontSize: "14px",
              }}
            >
              Create paint color using hex code, or color picker
            </Typography>
          </Stack>
          {/* <a
            href="https://www.youtube.com/watch?v=PFOoo9dt5Sk"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none" }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Grid
              sx={{
                display: "flex",
                flexDirection: "row",
                background: (theme) =>
                  isHovered
                    ? theme.palette.primary.shades[200]
                    : theme.palette.primary.shades[100],
                color: "white",
                padding: "2px 8px",
                borderRadius: "4px",
              }}
            >
              <PlayCircleIcon fontSize="small" />
              <Typography sx={{ fontSize: "14px", paddingLeft: "4px" }}>
                Tutorial
              </Typography>
            </Grid>
          </a> */}
        </Grid>

        <Grid
          sx={{
            display: "flex",
            flexDirection: "row",
            marginTop: "20px",
            marginBottom: "12px",
            justifyContent: "space-between",
          }}
        >
          {!incomingProduct && (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "32%",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    background: formik.values.color,
                    borderRadius: "10px",
                    height: "108px",
                    width: "100%",
                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                  }}
                />

                <TextField
                  {...textFieldProps}
                  id="color"
                  label="HEX Code"
                  name="color"
                  value={formik.values.color}
                  onChange={(e) =>
                    formik.setFieldValue("color", e.target.value)
                  }
                  sx={{
                    ...textFieldProps.sx,
                    width: "100%",
                    marginTop: "12px",
                    marginLeft: "10px",
                  }}
                />
              </Box>

              <Stack
                spacing={0}
                sx={{
                  width: "65%",
                  justifyContent: "center",
                }}
              >
                <TextField
                  {...textFieldProps}
                  id="name"
                  label="Paint Name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  sx={{ ...textFieldProps.sx, width: "100%" }}
                />

                <Autocomplete
                  id="brand"
                  freeSolo
                  value={formik.values.brand}
                  onChange={handleBrandChange}
                  options={Object.values(TopSuppliers)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...textFieldProps}
                      label="Brand Name"
                      placeholder="Brand Name"
                      name="brand"
                      sx={{ ...textFieldProps.sx, width: "100%" }}
                      InputLabelProps={{
                        shrink: true,
                        style: { backgroundColor: "white", padding: "0 4px" },
                      }}
                    />
                  )}
                />

                <Autocomplete
                  id="preset"
                  value={formik.values.preset}
                  onChange={(_event, newValue) => {
                    if (newValue) handlePresetChange(newValue);
                  }}
                  options={Object.keys(presetMaps)}
                  disableClearable
                  getOptionLabel={(option) => {
                    const preset = getPreset(option);
                    return preset.name;
                  }}
                  renderOption={(props, option) => {
                    const preset = getPreset(option);
                    return (
                      <li {...props}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar
                            sx={{ width: "25px", height: "20px", mr: 1 }}
                            src={preset.thumbnail}
                          />
                          {preset.name}
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...textFieldProps}
                      label="Preset Finish"
                      placeholder="Select a finish"
                      sx={{ ...textFieldProps.sx, width: "100%" }}
                      InputLabelProps={{
                        shrink: true,
                        style: { backgroundColor: "white", padding: "0 4px" },
                      }}
                    />
                  )}
                />
              </Stack>
            </>
          )}
        </Grid>

        {!incomingProduct && (
          <Grid
            sx={{
              marginBottom: "5px",
            }}
          >
            <Box
              sx={{
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: "4px",
                padding: "8px",
                position: "relative",
                maxWidth: "100%",
                "&:hover": {
                  borderColor: "text.primary",
                },
                "&:focus-within": {
                  borderColor: (theme) => theme.palette.primary.main,
                  borderWidth: "1px",
                },
                pt: 1,
                width: "calc(100%)",
                boxSizing: "border-box",
              }}
            >
              <FormLabel
                sx={{
                  position: "absolute",
                  top: "-10px",
                  left: "10px",
                  backgroundColor: "white",
                  px: 1,
                  fontSize: "0.75rem",
                }}
              >
                Color Picker
              </FormLabel>
              <ColorPicker
                onImageUpload={handleImageUpload}
                onColorExtracted={handleColorExtracted}
              />
            </Box>
          </Grid>
        )}

        <Grid
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "end",
            alignItems: "center",
            padding: "0px",
            marginTop: "5px",
            marginBottom: "0px",
          }}
        >
          <ActionButton
            disabled={saving}
            style={{ width: "150px" }}
            variant="contained"
            onClick={handleSubmit}
          >
            {saving ? "Saving..." : "Save Paint"}
          </ActionButton>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              width: "100px",
              height: "30px",
              background: "white",
              borderColor: (theme) => theme.palette.primary.light,
              "&:hover": {
                background: (theme) => theme.palette.primary.light,
                borderColor: (theme) => theme.palette.primary.main,
                color: "black",
              },
            }}
          >
            Close
          </Button>
        </Grid>
      </Stack>
    </AuthModal>
  );
};
